import { inject, Injectable, signal } from "@angular/core";
import { Auth, inMemoryPersistence, setPersistence, signInWithCustomToken, User as FirebaseUser, signOut } from "@angular/fire/auth";
import { UserClient } from "@clients/firestore/user.client";
import { environment } from "@environments/environment";
import { AnalyticsService } from "@services/analytics.service";
import { CustomTokenDto } from "@services/dtos/userData.dto";
import { ErrorBoundaryService } from "@services/error-boundary.service";
import { AuthState } from "@states/auth.state";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private firebaseAuth = inject(Auth);
  private userClient = inject(UserClient);
  private analyticsService = inject(AnalyticsService);
  private errorBoundaryService = inject(ErrorBoundaryService);
  private authState = inject(AuthState);

  private baseUrl = environment.apFunctions.baseUrl;

  isAuthenticating = signal(true);
  userLoggedOut = signal(false);

  setUserAsGuest() {
    this.authState.deleteCurrentUser();
    this.isAuthenticating.set(false);
  }

  async authenticateUser(idToken: string) {
    await setPersistence(this.firebaseAuth, inMemoryPersistence);

    try {
      const customToken = await this.getCustomToken(idToken);

      if (!customToken) {
        this.authState.deleteCurrentUser();
        this.analyticsService.removeUserId();
        return;
      }

      const firebaseUser = await this.signUserIn(customToken);
      if (!firebaseUser) {
        this.authState.deleteCurrentUser();
        this.analyticsService.removeUserId();
        return;
      }

      const user = await this.userClient.getUserById(firebaseUser.uid);
      this.authState.setCurrentUser(user);
      this.analyticsService.setUserId(user);
      this.isAuthenticating.set(false);
    } catch (error) {
      this.analyticsService.trackException(error as Error);
      this.errorBoundaryService.triggerError();
    }
  }

  private async getCustomToken(idToken: string): Promise<string | undefined> {
    const response = await fetch(`${this.baseUrl}/apiFunctionsApp/client/user-custom-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ idToken: idToken }),
    });

    const data: CustomTokenDto | undefined = await response.json();

    return data?.customToken;
  }

  private async signUserIn(customToken: string): Promise<FirebaseUser | undefined> {
    const userCredential = await signInWithCustomToken(this.firebaseAuth, customToken);

    return userCredential.user;
  }

  async signUserOut(): Promise<void> {
    try {
      await signOut(this.firebaseAuth);
      this.authState.deleteCurrentUser();
      this.analyticsService.removeUserId();
      this.userLoggedOut.set(true);
    } catch (error) {
      this.analyticsService.trackException(new Error("Firebase sign out failed: Network connection lost"));
      throw error;
    }
  }
}
